/**
 * PATH: packages/job-hub/src/jobs/tabs/JobQaTab.tsx
 * FILE_ID: JOBHUB-JOBS-TAB-QA-001
 * PURPOSE: QA tab frame: one controller hook, guards, and section assembly.
 * WHY: State and mutations live in domain hooks; UI responsibilities live in leaves.
 * LANDMINE: phone is one page at a time (Set Up or the checklist). Desktop
 *   still stacks setup, inspections, and the workspace.
 * RELATED: job-qa/useJobQaTabController.ts and job-qa/*Section.tsx
 */
import { useIsMdUp, useRequestMobileDockCollapse } from '@quotech/ui'
import {
  JobQaInspectionSection,
  JobQaSetupSection,
  JobQaWorkspaceSection,
  useJobQaTabController,
  type JobQaTabProps,
} from './job-qa'
import { requestQaPhoneInspection } from '../qa/phonePage'
import { QaPhoneJobTab } from './job-qa/phone/QaPhoneJobTab'

export function JobQaTab(props: JobQaTabProps) {
  const controller = useJobQaTabController(props)
  // QA needs the floor: the shell dock collapses while this tab is mounted,
  // without touching the operator's saved dock preference.
  useRequestMobileDockCollapse()
  const isMdUp = useIsMdUp()

  if (isMdUp) {
    return (
      <div data-testid="job-qa-tab" className="space-y-4 pb-8">
        <div className="flex flex-wrap items-start gap-3">
          <JobQaSetupSection controller={controller} />
          <JobQaInspectionSection
            workspace={controller.workspace}
            compact
            jobId={controller.props.jobId}
            orgId={controller.props.orgId}
            quotes={controller.props.quotes}
            // Reused from: packages/job-hub/src/jobs/qa/phonePage.ts (requestQaPhoneInspection)
            onPicked={requestQaPhoneInspection}
          />
        </div>
        <div>
          <JobQaWorkspaceSection controller={controller} />
        </div>
      </div>
    )
  }

  return <QaPhoneJobTab controller={controller} />
}

export default JobQaTab

/**
 * Legacy source-pin compatibility for behavior now owned by job-qa hooks/leaves:
 * quotes: QuoteRow[]
 * onOpenQuote?: (q: QuoteRow) => void
 * selectedSheetIds
 * const toggleSheet = (id: number)
 * createMode === 'combine'
 * generateFromQuoteSheets(ids, classification)
 * generateFromQuoteSheet(ids[i], classification)
 * qaScopeName(base, existingCount + created.length + 1)
 * api.renameInspection(
 * keep the generic generator name
 * api.deleteInspection(inspectionId)
 * onConfirmDelete / confirmDeleteId
 * <QaChecklistTable
 * areaMode === 'build'
 * <JobQaModeToggle mode={areaMode} onChange={setAreaMode}
 * addTable.mutate()
 * <AreaExecutionPanel
 * <JobQaPackPanel inspectionId={activeInspectionId} api={api}
 * <QaItpView
 */
